import { useSelector } from 'react-redux';

import Box from '@liquidweb-dev/undercurrent/Box';
import Stack, { type StackProps } from '@liquidweb-dev/undercurrent/Stack';
import ArrowUpRightIcon from '@liquidweb-dev/undercurrent/icons/lucide/ArrowUpRight';
import FolderIcon from '@liquidweb-dev/undercurrent/icons/lucide/Folder';
import GlobeIcon from '@liquidweb-dev/undercurrent/icons/lucide/Globe';
import HardDriveIcon from '@liquidweb-dev/undercurrent/icons/lucide/HardDrive';
import HouseIcon from '@liquidweb-dev/undercurrent/icons/lucide/House';
import RouteIcon from '@liquidweb-dev/undercurrent/icons/lucide/Route';
import ServerIcon from '@liquidweb-dev/undercurrent/icons/lucide/Server';
import ServerCogIcon from '@liquidweb-dev/undercurrent/icons/lucide/ServerCog';
import ShieldCheckIcon from '@liquidweb-dev/undercurrent/icons/lucide/ShieldCheck';
import StarIcon from '@liquidweb-dev/undercurrent/icons/lucide/Star';

import NavLink, {
	type NavLinkMainProps,
} from 'components/standard/NavLinkMain';
import LinkChip from 'components/standard/NavLinkMain/LinkChip';
import { SERVER_CATEGORIES } from 'components/standard/ServersList';
import { selectors as appconfigSelectors } from 'modules/appConfig';
import useAssetList from 'modules/queries/asset/useList';
import useCloudImageList from 'modules/queries/cloud/image/useList';
import { getPreloadKeyForPath } from 'modules/pagePreload/registry';
import useBrandStatusUrl from 'utility/hooks/useBrandStatusUrl';
import HookedLink from '../common/HookedLink';
import NavFooterLink from '../common/NavFooterLink';
import NavSection from '../common/NavSection';
import PrefetchNavLink from '../common/PrefetchNavLink';
import useLeftHandLayout, { navSections, type Path } from '../context';
import { useNavDialogsContext } from '../navDialogsContext';

const navLabels: Record<Path, string> = {
	'/': 'Home',
	'/projects': 'Projects',
	'/servers': 'My servers',
	'/servers/images': 'Server images',
	'/servers/scheduled-tasks': 'Scheduled tasks',
	'/services/load-balancers': 'Load Balancers',
	'/services/advanced-services': 'Advanced Services',
	'/storage/backups-and-recovery': 'Backups & Recovery',
	'/services/cloud-block-storage': 'Block Storage',
	'/services/object-storage': 'Object Storage',
	'/services/vpns': 'Cloud VPN',
	'/network/cloud-ip-addresses': 'Cloud IP Addresses',
	'/services/cloud-private-network': 'Cloud Private Network',
	'/services/firewall-rulesets': 'Cloud Firewall Rulesets',
	'/services/firewalls': 'Hardware Firewalls',
	'/services/premium-business-email': 'Premium Business Email',
	'/security/server-protection': 'Server Protection',
	'/domain': 'Domains List',
	'/domain/dns': 'DNS',
	'/domain/dns/reverse-dns': 'Reverse DNS',
	'/domain/ssl': 'SSL',
	'/domain/cloudflare': 'Cloudflare',
	'/network/global-private-fabric': 'Global Private Fabric',
};

function HookedNavLink({
	href,
	count = 0,
	...rest
}: NavLinkMainProps & { href: Path; count?: number }) {
	const { path } = useLeftHandLayout();

	const isActive = href !== undefined && path === href;
	const label = navLabels[href];
	const preloadKey = getPreloadKeyForPath(href);

	const LinkComponent = preloadKey ? PrefetchNavLink : HookedLink;
	const linkProps = preloadKey ? { href, preloadKey } : { href };

	return (
		<NavLink
			active={isActive}
			component={LinkComponent}
			{...linkProps}
			endAdornment={count > 0 && <LinkChip active={isActive}>{count}</LinkChip>}
			{...rest}
		>
			{label}
		</NavLink>
	);
}

export function LeftNavList(props: StackProps) {
	const { setIsFavoritesOpen } = useNavDialogsContext();
	const { navSectionsOpen, toggleNavSection } = useLeftHandLayout();
	const statusUrl = useBrandStatusUrl();

	const { data: advancedServicesAssets } = useAssetList({
		category: ['ManagedEngineering'],
		page_size: 1,
	});
	const { data: cloudSitesAssets } = useAssetList({
		category: ['CloudSites'],
		page_size: 1,
	});
	const { data: assetList } = useAssetList({
		page_size: 1,
		category: SERVER_CATEGORIES,
	});
	const { data: cloudImagesAssets } = useCloudImageList({ page_size: 1 });
	const { data: premiumBusinessEmailAssets } = useAssetList({
		type: ['BusinessEmail', 'HostedEmail'],
		page_size: 1,
	});

	const manageHostname = useSelector(appconfigSelectors.getManageHostname);

	const hasAdvancedServices = (advancedServicesAssets?.items?.length || 0) > 0;
	const cloudSitesUniqId = cloudSitesAssets?.items?.[0]?.uniq_id;
	const hasCloudSites = Boolean(cloudSitesUniqId);
	const serversCount = assetList?.item_total;
	const cloudImagesCount = cloudImagesAssets?.item_total;
	const hasPremiumBusinessEmail =
		(premiumBusinessEmailAssets?.items?.length || 0) > 0;

	return (
		<Stack
			flexDirection="column"
			justifyContent="space-between"
			alignItems="stretch"
			padding={100}
			gap={150}
			useFlexGap
			{...props}
		>
			<NavSection>
				<HookedNavLink
					startIcon={
						<HouseIcon
							sx={{ color: ({ vars }) => vars.palette.uc.icon.main }}
						/>
					}
					href="/"
				/>

				<HookedNavLink
					startIcon={
						<FolderIcon
							sx={{ color: ({ vars }) => vars.palette.uc.icon.main }}
						/>
					}
					href="/projects"
				/>

				<NavLink
					component="button"
					startIcon={<StarIcon />}
					onClick={() => setIsFavoritesOpen(true)}
					sx={{
						border: 0,
						backgroundColor: 'transparent',
						textAlign: 'left',
						cursor: 'pointer',
					}}
				>
					Favorites
				</NavLink>
			</NavSection>

			<NavSection
				label="Servers"
				open={navSectionsOpen.servers}
				onToggle={() => toggleNavSection('servers')}
				LabelIcon={ServerIcon}
			>
				{navSections.servers.map((href) => {
					if (href === '/servers') {
						return (
							<HookedNavLink key={href} href={href} count={serversCount} />
						);
					}

					if (href === '/servers/images') {
						return (
							<HookedNavLink key={href} href={href} count={cloudImagesCount} />
						);
					}

					return <HookedNavLink key={href} href={href} />;
				})}
			</NavSection>

			{(hasCloudSites || hasAdvancedServices || hasPremiumBusinessEmail) && (
				<NavSection
					label="Hosting"
					open={navSectionsOpen.hosting}
					onToggle={() => toggleNavSection('hosting')}
					LabelIcon={ServerCogIcon}
				>
					{hasCloudSites && (
						<NavLink
							href={`${manageHostname}/manage/sso.html?uniq_id=${cloudSitesUniqId}`}
							target="_blank"
							endAdornment={
								<ArrowUpRightIcon
									sx={({ size }) => ({ height: size.xS, width: size.xS })}
								/>
							}
						>
							Cloud Sites
						</NavLink>
					)}

					{hasAdvancedServices && (
						<HookedNavLink href="/services/advanced-services" />
					)}

					{hasPremiumBusinessEmail && (
						<HookedNavLink href="/services/premium-business-email" />
					)}
				</NavSection>
			)}

			<NavSection
				label="Cloud Storage"
				open={navSectionsOpen.storage}
				onToggle={() => toggleNavSection('storage')}
				LabelIcon={HardDriveIcon}
			>
				{navSections.storage.map((href) => (
					<HookedNavLink key={href} href={href} />
				))}
			</NavSection>

			<NavSection
				label="Network"
				open={navSectionsOpen.network}
				onToggle={() => toggleNavSection('network')}
				LabelIcon={RouteIcon}
			>
				{navSections.network.map((href) => (
					<HookedNavLink key={href} href={href} />
				))}
			</NavSection>

			<NavSection
				label="Security"
				open={navSectionsOpen.security}
				onToggle={() => toggleNavSection('security')}
				LabelIcon={ShieldCheckIcon}
			>
				{navSections.security.map((href) => (
					<HookedNavLink key={href} href={href} />
				))}
			</NavSection>

			<NavSection
				label="Domains"
				open={navSectionsOpen.domains}
				onToggle={() => toggleNavSection('domains')}
				LabelIcon={GlobeIcon}
			>
				{navSections.domains.map((href) => (
					<HookedNavLink key={href} href={href} />
				))}
			</NavSection>

			<Box
				sx={{
					flex: '0% 1 1', // Expand to fill
					marginTop: (theme) => `-${theme.spacing(200)}`, // But not twice the gap
				}}
			>
				{/* Divider spacing */}
			</Box>

			<Stack flexDirection="column" alignItems="stretch">
				<NavFooterLink href={statusUrl} target="_blank">
					Status Page
				</NavFooterLink>
			</Stack>
		</Stack>
	);
}

export default LeftNavList;
